// サイト共通のメタ情報と、静的ルートのページ別メタ定義。
//
// 詳細ページ（/news/:slug・/cases/:slug）は取得したコンテンツによって
// タイトルが変わるため、ここには載せず各コンポーネント側で <Seo /> を描画する。
// sitemap.xml の生成（scripts/generate-sitemap.js）もこのルート定義を参照する。

export const SITE_URL = "https://leaps-inc.com";
export const SITE_NAME = "LEAPS";
export const DEFAULT_OG_IMAGE = `${SITE_URL}/opengraph-image.png`;

export const DEFAULT_TITLE =
  "株式会社LEAPS｜AIとブロックチェーンのソリューションカンパニー";
export const DEFAULT_DESCRIPTION =
  "株式会社LEAPSは、AIやブロックチェーンを軸にしたソリューションカンパニーです。業務にAIエージェントを組み込むAIインテグレーションと、ブロックチェーン事業開発を通じて、企業のデジタル変革を推進します。";

// WordPressが描画する /media 配下は、末尾スラッシュ付きが正規URL。
// スラッシュ無しは301で付きへ飛ぶため、サイトマップにスラッシュ無しで
// 載せると全件が「ページにリダイレクトがあります」になる。
// Reactが描画するルートは逆に、スラッシュ無しがそのまま200を返す。
export const TRAILING_SLASH_PREFIXES = ["/media"];

/** サイトマップに載せる正規URLを組み立てる。 */
export function canonicalLoc(siteUrl, routePath) {
  const needsSlash = TRAILING_SLASH_PREFIXES.some(
    (p) => routePath === p || routePath.startsWith(`${p}/`),
  );
  const withSlash =
    needsSlash && !routePath.endsWith("/") ? `${routePath}/` : routePath;
  return `${siteUrl}${withSlash}`;
}

/** タイトルを「ページ名 | LEAPS」に整形する。トップだけはサイト名単独。 */
export function formatTitle(pageTitle) {
  if (!pageTitle) return DEFAULT_TITLE;
  if (pageTitle === DEFAULT_TITLE) return pageTitle;
  return `${pageTitle} | ${SITE_NAME}`;
}

// 静的ルートのメタ定義。changefreq/priority は sitemap.xml 用。
export const ROUTE_META = {
  "/": {
    title: DEFAULT_TITLE,
    description: DEFAULT_DESCRIPTION,
    priority: "1.0",
    changefreq: "weekly",
  },
  "/services": {
    title: "サービス",
    description:
      "LEAPSのサービス一覧です。AIエージェントを業務に組み込む「AIインテグレーション」と、企画から開発・グロースまで一貫して手がける「ブロックチェーン事業開発」で、企業のデジタル変革を支えます。",
    priority: "0.9",
    changefreq: "monthly",
  },
  "/services/ai-integration": {
    title: "AIインテグレーション",
    description:
      "既存の業務にAIエージェントを組み込み、業務改革から、それを支える基盤づくりまでを開発します。業務の整理、AIエージェントの設計・開発、データ基盤の整備と社内への定着まで、LEAPSが一貫して支援します。",
    priority: "0.9",
    changefreq: "monthly",
  },
  "/services/blockchain": {
    title: "ブロックチェーン事業開発",
    description:
      "ブロックチェーン領域の豊富な知見をもとに、プロダクトの企画から設計・開発、リリース後の成長までを一貫して手がけます。スマートコントラクト、Webアプリ、決済連携まで対応します。",
    priority: "0.9",
    changefreq: "monthly",
  },
  "/cases": {
    title: "実績・事例",
    description:
      "旅行・観光、イベント、アパレル、金融、出版、地域創生など、LEAPSがAI・ブロックチェーンを活用して手がけたプロダクトの企画・開発の実績一覧です。",
    priority: "0.8",
    changefreq: "monthly",
  },
  "/news": {
    title: "お知らせ",
    description: "株式会社LEAPSのプレスリリース・お知らせの一覧です。",
    priority: "0.7",
    changefreq: "weekly",
  },
  "/company": {
    title: "会社概要",
    description:
      "株式会社LEAPS（LEAPS Inc.）の会社概要です。2023年5月設立、東京都千代田区神田神保町。AIインテグレーションとブロックチェーン事業開発を手がけています。",
    priority: "0.6",
    changefreq: "yearly",
  },
  "/contact": {
    title: "無料相談・お問い合わせ",
    description:
      "AI・ブロックチェーンの活用やサービス開発について、お気軽にご相談ください。「まだ何も決まっていない」という段階でも構いません。2〜3営業日以内に担当者よりご連絡します。",
    priority: "0.7",
    changefreq: "yearly",
  },
  "/privacy": {
    title: "プライバシーポリシー",
    description: "株式会社LEAPSにおける個人情報の取り扱い方針をご説明します。",
    priority: "0.3",
    changefreq: "yearly",
  },
  // /media/ はWordPressが配信するため、Reactの静的ルートには持たない。
  // sitemapのメディア分もWordPressのRESTから取っている。
  "/diagnosis": {
    title: "3分でわかる無料AI診断",
    description:
      "かんたんな設問に答えるだけで、自社にどんなAI活用が向いているかが3分でわかります。費用はかかりません。",
    priority: "0.7",
    changefreq: "monthly",
  },
  "/document": {
    title: "資料ダウンロード",
    description:
      "AI導入支援のサービス概要・導入事例・料金感をまとめた資料を無料でお送りします。",
    priority: "0.7",
    changefreq: "monthly",
  },
  "/download": {
    title: "AIサービスの契約条件 比較表｜無料ダウンロード",
    description:
      "ChatGPT・Microsoft Copilot・Gemini・Claude の法人向けプランについて、学習利用・評価ボタンの扱い・保存期間・保存先と処理先・人によるレビューを、公式ドキュメントから確認して埋めた表です。2026年9月版。",
    priority: "0.8",
    changefreq: "monthly",
  },
  "/whitepaper": {
    title: "個人AIから組織AIへ｜ホワイトペーパー無料ダウンロード",
    description:
      "個人のAI活用が組織の成果に繋がらないのはなぜか。属人化・再現性・データ連携・ナレッジ・コストの5つの症状と、整えるべき5要素の実践ステップをまとめた全12ページの資料です。",
    priority: "0.8",
    changefreq: "monthly",
  },
  "/whitepaper/ai-case-studies": {
    title: "国内外34のAI導入事例サーベイ｜資料無料ダウンロード",
    description:
      "国内外のAI導入事例34件（31社）を、業務の種類ごとに1事例1ページでまとめた経営者向け資料です。導入前後の比較と効果の数字、効果が出た会社に共通する3つの条件、最初の一歩が分かります。全46ページ。",
    priority: "0.8",
    changefreq: "monthly",
  },
  "/consult": {
    title: "無料相談",
    description:
      "「組織でAIを使いこなしたい」「ChatGPTを入れたが効果が出ない」——そんな段階からご相談いただけます。オンラインでの個別相談は無料です。",
    priority: "0.7",
    changefreq: "monthly",
  },
};

/**
 * WordPress の rendered HTML から meta 用のプレーンテキストを作る。
 * タグと主要な文字参照を落とし、指定文字数で丸める。
 */
export function toPlainText(html, maxLength = 120) {
  if (!html) return "";
  const text = String(html)
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#0?39;/g, "'")
    .replace(/\[?&hellip;\]?/g, "…")
    .replace(/\s+/g, " ")
    .trim();
  return text.length > maxLength ? `${text.slice(0, maxLength)}…` : text;
}

/** pathname から静的ルートのメタを引く。詳細ページなど未定義のパスは null。 */
export function resolveRouteMeta(pathname) {
  // 末尾スラッシュを正規化（"/news/" と "/news" を同一視する）
  const path = pathname.length > 1 ? pathname.replace(/\/+$/, "") : pathname;
  return ROUTE_META[path] || null;
}
